import { useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, MenuItem, TextField } from '@mui/material';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import { useNavigate } from 'react-router-dom';
import { listEntranceExamStudents } from '../../api/exams';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { EntranceExamStudent, HandlingFeeFilter } from '../../types/exam';
import { EmptyRows, ExamListFilters, FeeStatusChip } from './examListShared';
import {
  EMPTY_EXAM_FILTERS,
  ExamListFilterValues,
  extractErrorMessage,
  formatMoney,
} from './examListUtils';

const person = (p: { firstName: string; lastName: string } | null) =>
  p ? `${p.firstName} ${p.lastName}` : '—';

/** CRM → Entrance Exam: students with the Registration Fee fully paid
 * (incl. SSCL) and at least one confirmed IMC professional handling fee
 * payment. Eligibility is decided entirely on the backend. */
export const EntranceExamPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<ExamListFilterValues>(EMPTY_EXAM_FILTERS);
  const [handlingFee, setHandlingFee] = useState<HandlingFeeFilter>('ALL');

  const listQuery = useQuery({
    queryKey: [
      'entrance-exam',
      paginationModel.page,
      paginationModel.pageSize,
      filters,
      handlingFee,
      selectedBranchId,
    ],
    queryFn: () =>
      listEntranceExamStudents({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: filters.search || undefined,
        universityId: filters.universityId || undefined,
        intakeId: filters.intakeId || undefined,
        counselorId: filters.counselorId || undefined,
        branchId: selectedBranchId,
        handlingFee,
      }),
    placeholderData: (prev) => prev,
  });

  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  const columns: GridColDef<EntranceExamStudent>[] = [
    {
      field: 'studentNumber',
      headerName: 'Student #',
      width: 120,
      valueGetter: (_v, row) => row.student.studentNumber,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => `${row.student.firstName} ${row.student.lastName}`,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      valueGetter: (_v, row) => row.student.phone,
    },
    {
      field: 'branch',
      headerName: 'Branch',
      width: 120,
      valueGetter: (_v, row) => row.branch?.name ?? '—',
    },
    {
      field: 'university',
      headerName: 'University',
      width: 180,
      valueGetter: (_v, row) => row.university?.name ?? '—',
    },
    {
      field: 'course',
      headerName: 'Programme',
      width: 170,
      valueGetter: (_v, row) => row.course?.name ?? '—',
    },
    {
      field: 'intake',
      headerName: 'Intake',
      width: 120,
      valueGetter: (_v, row) => row.intake?.name ?? '—',
    },
    {
      field: 'counselor',
      headerName: 'Counselor',
      width: 150,
      valueGetter: (_v, row) => person(row.counselor),
    },
    {
      field: 'registrationFee',
      headerName: 'Registration Fee',
      width: 140,
      sortable: false,
      renderCell: (params) => <FeeStatusChip fee={params.row.registrationFee} />,
    },
    {
      field: 'handlingFee',
      headerName: 'Handling Fee',
      width: 150,
      sortable: false,
      renderCell: (params) => <FeeStatusChip fee={params.row.handlingFee} />,
    },
    {
      field: 'handlingPaid',
      headerName: 'Handling paid',
      width: 130,
      valueGetter: (_v, row) => formatMoney(row.handlingFee.paidAmount, row.handlingFee.currency),
    },
    {
      field: 'handlingOutstanding',
      headerName: 'Handling outstanding',
      width: 160,
      valueGetter: (_v, row) =>
        formatMoney(row.handlingFee.outstandingAmount, row.handlingFee.currency),
    },
  ];

  const hasFilters =
    !!filters.search ||
    !!filters.universityId ||
    !!filters.intakeId ||
    !!filters.counselorId ||
    handlingFee !== 'ALL';

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={QuizIcon}
        title="Entrance Exam"
        subtitle="Students eligible for the Entrance Exam."
      />

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Eligible when the Registration Fee is fully paid (including SSCL) and the IMC Professional
        Handling Fee is fully or partially paid. Only confirmed payments count. An uploaded payment
        slip awaiting confirmation does not.
      </Alert>

      <ExamListFilters
        values={filters}
        onChange={(values) => {
          setFilters(values);
          resetPage();
        }}
      >
        <TextField
          select
          size="small"
          label="Handling fee"
          sx={{ minWidth: 160 }}
          value={handlingFee}
          onChange={(e) => {
            setHandlingFee(e.target.value as HandlingFeeFilter);
            resetPage();
          }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="FULLY_PAID">Fully paid</MenuItem>
          <MenuItem value="PARTIALLY_PAID">Partially paid</MenuItem>
        </TextField>
      </ExamListFilters>

      {listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load Entrance Exam students: {extractErrorMessage(listQuery.error)}
        </Alert>
      ) : (
        <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{ toolbar: TableToolbar, noRowsOverlay: EmptyRows }}
            slotProps={{
              noRowsOverlay: {
                message: hasFilters
                  ? 'No eligible students match these filters.'
                  : 'No students are eligible for the Entrance Exam yet.',
              },
            }}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/students/${params.row.studentId}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}
    </Box>
  );
};
